// Reglas de las promociones automáticas, con el mismo criterio de `validar_descuento_de_venta` y
// `promocion_superpuesta`: vigencia por fecha y día de Colombia, alcance por producto o categoría.

/** A qué se aplica una promoción: un producto o una categoría del catálogo. */
export interface AlcancePromocion {
  productoId: string | null;
  categoria: string | null;
}

/** Lo que las reglas necesitan de un descuento del negocio. Sin días ni fechas no es automático. */
export interface ReglaDePromocion {
  id: string;
  nombre: string;
  activo: boolean;
  /** YYYY-MM-DD, o null si no tiene ese límite. */
  desde: string | null;
  hasta: string | null;
  /** 1 = lunes … 7 = domingo; vacío, todos los días. */
  dias: number[];
  alcance: AlcancePromocion[];
}

export function esAutomatica(p: Pick<ReglaDePromocion, 'desde' | 'hasta' | 'dias'>): boolean {
  return p.dias.length > 0 || p.desde !== null || p.hasta !== null;
}

/** Día ISO de una fecha YYYY-MM-DD: 1 = lunes … 7 = domingo. */
export function diaDeLaSemana(fecha: string): number {
  const dia = new Date(`${fecha.slice(0, 10)}T12:00:00Z`).getUTCDay();
  return dia === 0 ? 7 : dia;
}

/** Si la promoción vale en esa fecha (YYYY-MM-DD de Colombia). */
export function vigenteEn(p: Pick<ReglaDePromocion, 'desde' | 'hasta' | 'dias'>, fecha: string): boolean {
  if (p.desde !== null && fecha < p.desde) return false;
  if (p.hasta !== null && fecha > p.hasta) return false;
  return p.dias.length === 0 || p.dias.includes(diaDeLaSemana(fecha));
}

/** Si el descuento cubre un producto, por él mismo o por su categoría. Sin alcance, cubre todos. */
export function cubreProducto(p: Pick<ReglaDePromocion, 'alcance'>, productoId: string, categoria?: string): boolean {
  const cat = categoria?.toUpperCase();
  if (p.alcance.length === 0) return true;
  return p.alcance.some((a) => a.productoId === productoId || (a.categoria !== null && a.categoria.toUpperCase() === cat));
}

/** La promoción activa y vigente que cubre un producto en esa fecha, salvo las que se quitaron en la línea. */
export function promocionPara<T extends ReglaDePromocion>(
  promociones: T[],
  productoId: string,
  categoria: string | undefined,
  fecha: string,
  quitadas: string[] = []
): T | null {
  return (
    promociones.find(
      (p) => p.activo && esAutomatica(p) && !quitadas.includes(p.id) && vigenteEn(p, fecha) && cubreProducto(p, productoId, categoria)
    ) ?? null
  );
}

/**
 * El otro automático activo que toca los mismos productos con fechas y días que se cruzan, o null. Uno sin
 * alcance toca todos; los vencidos no cuentan. `categoriaDe` da la categoría de un producto del alcance.
 */
export function promocionSuperpuesta<T extends ReglaDePromocion>(
  nueva: Omit<ReglaDePromocion, 'activo' | 'id'> & { id: string | null },
  otras: T[],
  categoriaDe: (productoId: string) => string | undefined,
  hoy: string
): T | null {
  if (!esAutomatica(nueva)) return null;
  const categoriaDeAlcance = (a: AlcancePromocion) => (a.categoria ?? (a.productoId ? categoriaDe(a.productoId) : undefined))?.toUpperCase();
  const tocan = (a: AlcancePromocion, b: AlcancePromocion) =>
    (a.productoId !== null && a.productoId === b.productoId) ||
    (a.categoria !== null && a.categoria.toUpperCase() === categoriaDeAlcance(b)) ||
    (b.categoria !== null && b.categoria.toUpperCase() === categoriaDeAlcance(a));
  return (
    otras.find(
      (o) =>
        o.id !== nueva.id &&
        o.activo &&
        esAutomatica(o) &&
        (o.hasta === null || o.hasta >= hoy) &&
        (nueva.desde ?? '0000-00-00') <= (o.hasta ?? '9999-99-99') &&
        (o.desde ?? '0000-00-00') <= (nueva.hasta ?? '9999-99-99') &&
        (nueva.dias.length === 0 || o.dias.length === 0 || nueva.dias.some((d) => o.dias.includes(d))) &&
        (nueva.alcance.length === 0 || o.alcance.length === 0 || nueva.alcance.some((a) => o.alcance.some((b) => tocan(a, b))))
    ) ?? null
  );
}
